import React, { useState, useEffect, useRef } from 'react';
import { Search, X, TrendingUp, Clock, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    if (query.length > 2) {
      performSearch(query);
    } else {
      setResults([]);
    }
  }, [query]);

  const performSearch = async (searchQuery) => {
    // Mock search - in production, this would call your search API
    const mockResults = [
      {
        id: 1,
        type: 'station',
        title: 'Station #45 - Delhi NCR',
        description: 'Online, 4 connectors available',
        path: '/network/stations/45',
      },
      {
        id: 2,
        type: 'incident',
        title: 'Incident #123 - Charger Malfunction',
        description: 'Reported 2 hours ago, In Progress',
        path: '/ops/incidents/123',
      },
      {
        id: 3,
        type: 'document',
        title: 'SLA Credits Report - October 2024',
        description: 'Generated on Nov 1, 2024',
        path: '/planning/sla-credits',
      },
    ].filter(r => 
      r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.description.toLowerCase().includes(searchQuery.toLowerCase())
    );

    setResults(mockResults);
  };

  const handleResultClick = (result) => {
    addToRecentSearches(result);
    navigate(result.path);
    setOpen(false);
    setQuery('');
  };

  const addToRecentSearches = (result) => {
    const newRecent = [result, ...recentSearches.filter(r => r.id !== result.id)].slice(0, 5);
    setRecentSearches(newRecent);
    localStorage.setItem('recentSearches', JSON.stringify(newRecent));
  };

  const getIcon = (type) => {
    switch (type) {
      case 'station': return TrendingUp;
      case 'incident': return Clock;
      case 'document': return FileText;
      default: return FileText;
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
        style={{
          backgroundColor: 'var(--bg-tertiary)',
          color: 'var(--text-secondary)',
        }}
        title="Search (Cmd+/)"
      >
        <Search size={16} />
        <span className="hidden md:inline text-sm">Search...</span>
        <kbd className="hidden md:inline px-2 py-0.5 text-xs rounded" style={{ backgroundColor: 'var(--bg-hover)' }}>
          ⌘/
        </kbd>
      </button>
    );
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed left-1/2 top-[15%] -translate-x-1/2 w-full max-w-2xl"
        >
          <div
            className="rounded-lg border shadow-2xl overflow-hidden"
            style={{
              backgroundColor: 'var(--bg-primary)',
              borderColor: 'var(--border-primary)',
            }}
          >
            {/* Search Input */}
            <div className="flex items-center border-b" style={{ borderColor: 'var(--border-primary)' }}>
              <Search className="ml-4" size={20} style={{ color: 'var(--text-tertiary)' }} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search stations, incidents, documents..."
                className="flex-1 h-14 px-4 bg-transparent border-0 outline-none"
                style={{ color: 'var(--text-primary)' }}
              />
              <button
                onClick={() => setOpen(false)}
                className="mr-4 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X size={20} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-96 overflow-y-auto">
              {query.length <= 2 && recentSearches.length > 0 && (
                <div className="p-4">
                  <h3 className="text-sm font-medium mb-3" style={{ color: 'var(--text-secondary)' }}>
                    Recent Searches
                  </h3>
                  {recentSearches.map(result => (
                    <ResultItem
                      key={result.id}
                      result={result}
                      onClick={() => handleResultClick(result)}
                      getIcon={getIcon}
                    />
                  ))}
                </div>
              )}

              {query.length > 2 && results.length === 0 && (
                <div className="p-8 text-center" style={{ color: 'var(--text-tertiary)' }}>
                  No results found for "{query}"
                </div>
              )}

              {results.length > 0 && (
                <div className="p-2">
                  {results.map(result => (
                    <ResultItem
                      key={result.id}
                      result={result}
                      onClick={() => handleResultClick(result)}
                      getIcon={getIcon}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div
              className="flex items-center justify-between px-4 py-2 border-t text-xs"
              style={{
                borderColor: 'var(--border-primary)',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-tertiary)',
              }}
            >
              <span>Tip: Use Cmd+/ to search anytime</span>
              <span>ESC to close</span>
            </div>
          </div>
        </motion.div>

        <div className="absolute inset-0" onClick={() => setOpen(false)} />
      </div>
    </AnimatePresence>
  );
};

const ResultItem = ({ result, onClick, getIcon }) => {
  const Icon = getIcon(result.type);
  
  return (
    <button
      onClick={onClick}
      className="w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors"
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <div className="p-2 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
        <Icon size={16} style={{ color: 'var(--text-secondary)' }} />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
          {result.title}
        </h4>
        <p className="text-sm truncate" style={{ color: 'var(--text-secondary)' }}>
          {result.description}
        </p>
      </div>
    </button>
  );
};

export default GlobalSearch;